/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { Query } from 'react-apollo'
import gql from 'graphql-tag'

import {
  Picklist,
  Spinner,
  Warning
} from '../'

const QUERY_WORKFLOWS = gql`
  query {
    workflows(orderBy: "name") {
      edges {
        node {
          id
          name
          version
        }
      }
    }
  }
`

export default class WorkflowPicklist extends React.Component {
  static propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    default: PropTypes.string,
    value: PropTypes.string,
    closeDropdownOnSelect: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    minWidth: PropTypes.number,
    symbol: PropTypes.string,
    inline: PropTypes.bool,
    className: PropTypes.string
  }

  static defaultProps = {
    default: 'All'
  }

  getName = (nameWithVersion) => nameWithVersion.substring(0, nameWithVersion.indexOf('(v')).trim()
  getVersion = (nameWithVersion) => {
    let partInBrackets = nameWithVersion.match('(v[1234567890])')[0] // e.g. for "Deductions (v2)", will get 'v2'
    let versionNumber = parseInt(partInBrackets.match('[1234567890]'), 10)  // e.g. for 'v2', will return'2'
    return versionNumber
  }

  getCurrentSelectedValue = (options, value) => {
    let currentRecord = options.find(record => record.node.id === value)
    return currentRecord.node.version > 1  // if workflow has version > 1 specified, show it
      ? `${currentRecord.node.name} (v${currentRecord.node.version})`
      : currentRecord.node.name
  }

  getPicklistOptions = (options) => (
    options.map(option => (
      option.node.version > 1  // if workflow has version > 1 specified, show it
        ? `${option.node.name} (v${option.node.version})`
        : option.node.name
    ))
  )

  handleChange = (value, options) => (
    value.includes('(v')
      ? this.props.onChange(options.find(option => (  // find workflow option with corresponding name & version
          option.node.name === this.getName(value) &&
          option.node.version === this.getVersion(value)
        )).node.id)
      : value === ''
        ? this.props.onChange(value)
        : this.props.onChange(options.find(option => (  // find workflow option with corresponding name & version = 1
          option.node.name === value &&
          option.node.version === 1
        )).node.id)
  )

  render() {
    let { id, default: defaultValue, value, disabled, className } = this.props

    return (
      <Query query={QUERY_WORKFLOWS}>
        {({ loading, error, data }) => {
          if (loading) {
            return <Spinner className='slds-is-relative slds-m-left_large' size='x-small' />
          }
          if (error) {
            return (
              <Warning className='slds-m-left_small' iconSize='x-small'>
                Failed to load options.
              </Warning>
            )
          }

          let options = data.workflows.edges
          if (defaultValue && options[0].node.name !== defaultValue) {
            let defaultOption = {
              node: {
                id: '',
                name: defaultValue,
                version: 1
              }
            }
            options.unshift(defaultOption)
          }

          return (
            <Picklist
              id={id}
              options={this.getPicklistOptions(options)}
              default={defaultValue}
              symbol='chevrondown'
              value={this.getCurrentSelectedValue(options, value)}
              onChange={value => this.handleChange(value, options)}
              closeDropdownOnSelect={true}
              inline={true}
              disabled={disabled || options.length === 0}  // length = 0 if the 'field' prop is not a field in query results
              className={className}
            />
          )
        }}
      </Query>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/workflow-picklist/index.js